"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SignUpForm() {
  return;
}
function SignUpFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light/10 border border-accent/30 rounded-md">
        <p className="text-foreground-primary text-center text-lg mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mx-auto block bg-transparent border-accent text-accent hover:bg-accent hover:text-foreground-accent">
          Submit Another Registration
        </Button>
      </div>;
  }
  return <div>
      <h2 ref={titleRef} className={`text-foreground-primary text-4xl md:text-5xl font-bold text-center mb-2 transition-all duration-[var(--duration-slow)] ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        Conference Sign‑Up Form
      </h2>
      <div className={`w-24 h-1 bg-accent mx-auto mb-12 transition-all duration-[var(--duration-slow)] delay-100 ${titleVisible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"}`} />

      <Form onSubmit={handleSubmit}>
        <div ref={formRef} className={`space-y-6 transition-all duration-[var(--duration-slow)] delay-200 ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Name must be at least 2 characters"
          },
          maxLength: {
            value: 100,
            message: "Name must be less than 100 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="Jane Doe" {...field} className="bg-primary-light/5 border-border text-foreground-primary placeholder:text-muted-foreground focus:border-accent" />
                </FormControl>
                <FormDescription className="text-muted-foreground text-xs">
                  Enter your first and last name.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jane@example.com" {...field} className="bg-primary-light/5 border-border text-foreground-primary placeholder:text-muted-foreground focus:border-accent" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
            message: "Please enter a valid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+1 555‑123‑4567" {...field} className="bg-primary-light/5 border-border text-foreground-primary placeholder:text-muted-foreground focus:border-accent" />
                </FormControl>
                <FormDescription className="text-muted-foreground text-xs">
                  Optional – we may contact you about schedule changes.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="organization" rules={{
          required: "Organization is required",
          minLength: {
            value: 2,
            message: "Organization must be at least 2 characters"
          },
          maxLength: {
            value: 100,
            message: "Organization must be less than 100 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Organization / Company</FormLabel>
                <FormControl>
                  <Input placeholder="Acme Corp." {...field} className="bg-primary-light/5 border-border text-foreground-primary placeholder:text-muted-foreground focus:border-accent" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="job_title" rules={{
          maxLength: {
            value: 100,
            message: "Job title must be less than 100 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Job Title</FormLabel>
                <FormControl>
                  <Input placeholder="Marketing Manager" {...field} className="bg-primary-light/5 border-border text-foreground-primary placeholder:text-muted-foreground focus:border-accent" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="interest_topics" render={({
          field
        }) => <FormItem>
                <FormLabel>Topics of Interest</FormLabel>
                <FormDescription className="text-muted-foreground text-xs mb-3">
                  Select any topics you'd like to hear more about.
                </FormDescription>
                <div className="space-y-3">
                  {["Technology Innovation", "Sustainability", "Leadership", "Marketing Strategies", "Networking"].map((topic, i) => <div key={topic} data-index={i} className="flex items-center gap-3">
                      <Checkbox checked={field.value?.includes(topic)} onCheckedChange={checked => {
                const updatedValue = checked ? [...(field.value || []), topic] : (field.value || []).filter((val: string) => val !== topic);
                field.onChange(updatedValue);
              }} className="border-border data-[state=checked]:bg-accent data-[state=checked]:border-accent data-[state=checked]:text-foreground-accent" />
                      <label className="text-foreground-primary text-sm cursor-pointer">
                        {topic}
                      </label>
                    </div>)}
                </div>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="additional_message" rules={{
          maxLength: {
            value: 500,
            message: "Message must be less than 500 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Additional Message</FormLabel>
                <FormControl>
                  <Textarea placeholder="Any special requirements or questions?" {...field} className="bg-primary-light/5 border-border text-foreground-primary placeholder:text-muted-foreground focus:border-accent min-h-[120px] resize-none" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
              <p className="text-destructive text-sm">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-foreground-accent font-semibold hover:bg-accent-hover active:bg-accent-active hover:scale-[1.03] active:scale-100 transition-all duration-[var(--duration-medium)] shadow-md">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Submitting...
              </> : "Register Now"}
          </Button>
        </div>
      </Form>
    </div>;
}